import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Database,
  Users,
  KeyRound,
  Check,
  ChevronDown,
  ArrowLeft,
} from 'lucide-react';
import AuthNavbar from '../common/AuthNavbar';
import {
  registerOrganizationWithGoogle,
  setTenantSession,
  PRODUCT_NAME,
} from '../../lib/tenant';
import { billingApi, type ApiPlan, type BillingTerm } from '../../api';
import { setAuthToken } from '../../api/client';
import { setCurrentUser } from '../../lib/permissions';
import { openRazorpayCheckout } from '../../lib/razorpay';
import { planItemLabels } from '../../lib/planEntitlements';
import { defaultBillingTerm, formatPlanPrice, getPricingOptions, lowestOptionPrice, normalizeBillingTerm, resolvePlanPrice, TERM_LABELS } from '../../lib/planPricing';
import { setSubscriptionCache } from '../../lib/subscription';
import { PlanCheckoutSummary, PlanDurationPicker } from '../common/PlanDurationPicker';
import GoogleAuthButton, { useGoogleClientId } from '../common/GoogleAuthButton';
import { useScrollToTop } from '../../lib/useScrollToTop';

const BENEFITS = [
  { icon: Database, title: 'Isolated workspace', text: 'Your data lives in a dedicated, secure tenant — never mixed with others.' },
  { icon: Users, title: 'Unlimited team', text: 'Invite admins with fine-grained, per-module read/write permissions.' },
  { icon: ShieldCheck, title: 'Bank-grade security', text: 'Encrypted storage and role-based access control out of the box.' },
];

const STEPS = ['Choose plan', 'Billing term', 'Account details'] as const;
const STEP_SHORT = ['Plan', 'Term', 'Account'] as const;

function StepProgress({ active }: { active: number }) {
  return (
    <nav aria-label="Signup progress" className="mb-5 sm:mb-8">
      <ol className="flex w-full items-start">
        {STEPS.map((label, i) => {
          const done = i < active;
          const current = i === active;
          const leftDone = active >= i;
          const rightDone = active > i;
          return (
            <li key={label} className="relative flex min-w-0 flex-1 flex-col items-center">
              {/* Lines align to circle center — labels sit below without shifting them */}
              {i > 0 ? (
                <span
                  aria-hidden
                  className={`absolute left-0 right-1/2 top-4 h-0.5 -translate-y-1/2 sm:top-5 ${
                    leftDone ? 'bg-emerald-500' : 'bg-zinc-200'
                  }`}
                />
              ) : null}
              {i < STEPS.length - 1 ? (
                <span
                  aria-hidden
                  className={`absolute left-1/2 right-0 top-4 h-0.5 -translate-y-1/2 sm:top-5 ${
                    rightDone ? 'bg-emerald-500' : 'bg-zinc-200'
                  }`}
                />
              ) : null}
              <span
                className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold shadow-sm ring-2 transition-all duration-300 sm:h-10 sm:w-10 sm:text-sm ${
                  done
                    ? 'bg-emerald-600 text-white ring-emerald-200'
                    : current
                      ? 'bg-emerald-100 text-emerald-700 ring-emerald-300'
                      : 'bg-zinc-100 text-zinc-500 ring-zinc-200'
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3} /> : i + 1}
              </span>
              <span
                className={`mt-1.5 max-w-full px-0.5 text-center text-[10px] font-medium leading-tight sm:mt-2 sm:text-xs ${
                  current ? 'font-semibold text-emerald-700' : done ? 'text-zinc-700' : 'text-zinc-400'
                }`}
              >
                <span className="sm:hidden">{STEP_SHORT[i]}</span>
                <span className="hidden sm:inline">{label}</span>
              </span>
            </li>
          );
        })}
      </ol>
      <p className="mt-2 text-center text-[11px] font-medium text-zinc-500 sm:hidden">
        Step {active + 1} of {STEPS.length}:{' '}
        <span className="font-semibold text-emerald-700">{STEPS[active]}</span>
      </p>
    </nav>
  );
}

export default function RegisterOrganization() {
  const [searchParams] = useSearchParams();
  const initialPlan = searchParams.get('plan')?.trim() || '';
  const initialTerm = searchParams.get('term')?.trim() as BillingTerm | '';
  const navigate = useNavigate();
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlan);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [trialSecretKey, setTrialSecretKey] = useState('');
  const [autoPay, setAutoPay] = useState(true);
  const [selectedBillingTerm, setSelectedBillingTerm] = useState<BillingTerm>(
    normalizeBillingTerm(initialTerm || 'monthly')
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState<{
    intentId: string;
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  } | null>(null);
  const [showTrialKey, setShowTrialKey] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const googleClientId = useGoogleClientId();

  useScrollToTop([currentStep]);

  useEffect(() => {
    let cancelled = false;
    setPlansLoading(true);
    void billingApi
      .listPlans()
      .then((res) => {
        if (cancelled) return;
        setPlans(res.plans);
        setSelectedPlanId((prev) => {
          if (prev && res.plans.some((p) => p.planId === prev)) return prev;
          return res.plans[0]?.planId || '';
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedPlan = plans.find((p) => p.planId === selectedPlanId);

  useEffect(() => {
    if (!selectedPlan) return;
    if (initialTerm) {
      const term = normalizeBillingTerm(initialTerm);
      const ok = getPricingOptions(selectedPlan).some((o) => o.term === term);
      if (ok) {
        setSelectedBillingTerm(term);
        return;
      }
    }
    setSelectedBillingTerm(defaultBillingTerm(selectedPlan));
  }, [selectedPlanId, selectedPlan, initialTerm]);

  useEffect(() => {
    if (trialSecretKey.trim()) setShowTrialKey(true);
  }, [trialSecretKey]);

  const payingWithTrial = Boolean(trialSecretKey.trim());
  const selectedPrice = resolvePlanPrice(selectedPlan, selectedBillingTerm);

  const passwordsMatch = confirm.length > 0 && password === confirm;

  const finishAuth = (res: { token: string; user: { id: string; email: string; name: string; role: string }; organization: { id: string; name: string; slug: string; planId: string | null; subscriptionStatus: string } | null }) => {
    setAuthToken(res.token);
    setCurrentUser(res.user as Parameters<typeof setCurrentUser>[0]);
    localStorage.setItem('isLoggedIn', 'true');
    if (res.organization) {
      setTenantSession({
        id: res.organization.id,
        name: res.organization.name,
        slug: res.organization.slug,
        planId: res.organization.planId,
        subscriptionStatus: res.organization.subscriptionStatus as 'active' | 'pending_payment' | 'past_due',
      });
    }
    navigate('/dashboard');
  };

  const validateStep1 = () => {
    if (!selectedPlanId) {
      setError('Please select a subscription plan.');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    setError('');
    return true;
  };

  const validateStep3 = () => {
    if (!name.trim()) {
      setError('Organization name is required.');
      return false;
    }
    if (!email.trim()) {
      setError('Admin email is required.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return false;
    }
    if (!password) {
      setError('Enter a password.');
      return false;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return false;
    }
    if (trialSecretKey.trim() && trialSecretKey.trim().length !== 6) {
      setError('Trial code must be exactly 6 characters.');
      return false;
    }
    const phoneDigits = phone.replace(/\D/g, '');
    if (!trialSecretKey.trim() && phoneDigits.length > 0 && phoneDigits.length < 10) {
      setError('Enter a valid 10-digit mobile number.');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (currentStep === 0 && !validateStep1()) return;
    if (currentStep === 1 && !validateStep2()) return;
    if (currentStep === 2 && !validateStep3()) return;
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep3()) return;
    setLoading(true);
    setError('');
    try {
      if (pendingCheckout) {
        const completed = await billingApi.completeRegistration(pendingCheckout);
        setPendingCheckout(null);
        finishAuth(completed);
        return;
      }

      const res = await billingApi.startRegistration({
        name,
        adminEmail: email,
        adminPhone: phone.replace(/\D/g, '') || undefined,
        password,
        planId: selectedPlanId,
        billingTerm: selectedBillingTerm,
        trialSecretKey: trialSecretKey.trim() || undefined,
        autoPay,
      });
      if ('skipPayment' in res && res.skipPayment) {
        finishAuth(res);
        return;
      }
      if (!('intentId' in res)) throw new Error('Invalid registration response.');
      const payment = await openRazorpayCheckout({
        keyId: res.razorpayKeyId,
        orderId: res.order.id,
        amount: res.order.amount,
        currency: res.order.currency,
        name: PRODUCT_NAME,
        description: `${res.plan.name} subscription`,
        prefill: { name, email, contact: phone.replace(/\D/g, '') || undefined },
        paymentMode: res.paymentMode,
        demo: res.order.demo,
      });
      if (!payment) {
        setError('Payment was cancelled.');
        return;
      }
      const checkout = {
        intentId: res.intentId,
        razorpay_order_id: payment.orderId,
        razorpay_payment_id: payment.paymentId,
        razorpay_signature: payment.signature,
      };
      try {
        const completed = await billingApi.completeRegistration(checkout);
        setPendingCheckout(null);
        finishAuth(completed);
      } catch (completeErr) {
        setPendingCheckout(checkout);
        throw completeErr;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async (credential: string) => {
    if (!selectedPlanId) {
      setError('Select a subscription plan first, then continue with Google.');
      return;
    }
    if (!name.trim()) {
      setError('Enter your organization name first, then continue with Google.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const phoneDigits = phone.replace(/\D/g, '');
      const org = await registerOrganizationWithGoogle({
        credential,
        name,
        planId: selectedPlanId,
        trialSecretKey: trialSecretKey.trim() || undefined,
      });
      if (org.subscriptionStatus === 'active') {
        navigate('/dashboard');
        return;
      }

      const session = await billingApi.subscriptionOrder({
        planId: selectedPlanId,
        billingTerm: selectedBillingTerm,
        autoPay,
      });
      if ('skipPayment' in session && session.skipPayment) {
        setSubscriptionCache(session.subscription);
        setTenantSession({
          id: session.organization.id,
          name: session.organization.name,
          slug: session.organization.slug,
          planId: session.organization.planId,
          subscriptionStatus: session.organization.subscriptionStatus,
        });
        navigate('/dashboard');
        return;
      }

      const payment = await openRazorpayCheckout({
        keyId: session.razorpayKeyId,
        orderId: session.order.id,
        amount: session.order.amount,
        currency: session.order.currency,
        name: PRODUCT_NAME,
        description: `${session.plan.name} subscription`,
        prefill: {
          name: org.name,
          email: org.adminEmail,
          contact: phoneDigits || undefined,
        },
        paymentMode: session.paymentMode,
        demo: session.order.demo,
        notes: { planId: selectedPlanId, billingTerm: selectedBillingTerm, action: session.action },
      });
      if (!payment) {
        setError('Payment was cancelled. Your account is saved, so sign in again to finish checkout.');
        return;
      }

      const verified = await billingApi.verifySubscriptionPayment({
        paymentId: session.paymentId,
        razorpay_order_id: payment.orderId,
        razorpay_payment_id: payment.paymentId,
        razorpay_signature: payment.signature,
      });
      setSubscriptionCache(verified.subscription);
      setTenantSession({
        id: verified.organization.id,
        name: verified.organization.name,
        slug: verified.organization.slug,
        planId: verified.organization.planId,
        subscriptionStatus: verified.organization.subscriptionStatus,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-up failed.');
    } finally {
      setLoading(false);
    }
  };

  const fieldWrap =
    'mt-1.5 flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2.5 transition focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/15 sm:rounded-xl sm:px-3';
  const fieldInput = 'h-10 w-full border-0 bg-transparent text-[13px] text-zinc-900 outline-none placeholder:text-zinc-400 sm:h-12 sm:text-sm';

  return (
    <div className="min-h-screen bg-zinc-50 font-sans">
      <AuthNavbar />

      <main className="mx-auto w-full min-w-0 max-w-5xl overflow-x-clip px-3 pb-12 pt-[calc(3rem+env(safe-area-inset-top,0px)+0.75rem)] sm:px-6 sm:pb-20 sm:pt-[calc(4rem+env(safe-area-inset-top,0px)+2rem)] lg:pt-12">
        <header className="mb-5 max-w-3xl text-center sm:mb-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700 sm:text-[11px]">Sign up</p>
          <h1 className="mt-2 font-display text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl lg:text-3xl">
            Create your workspace
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-zinc-600 sm:text-sm">
            Get started with {PRODUCT_NAME} in 3 easy steps.
          </p>
        </header>

        {/* Mobile summary card */}
        {selectedPlan && selectedPrice != null && (
          <div className="mb-5 lg:hidden">
            <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 text-white shadow-lg">
              <div className="border-b border-zinc-800 px-4 py-3 sm:px-5 sm:py-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400">{PRODUCT_NAME}</p>
                <h2 className="mt-1 font-display text-base font-semibold leading-snug sm:text-lg">Everything you need on day one</h2>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Your selection</p>
                <div className="mt-2 flex items-baseline justify-between">
                  <p className="text-[13px] font-bold text-white sm:text-sm">
                    {selectedPlan.name} · <span className="text-[11px] font-normal text-zinc-400 sm:text-xs">{TERM_LABELS[selectedBillingTerm]}</span>
                  </p>
                  <p className="text-lg font-bold text-emerald-400 sm:text-xl">{formatPlanPrice(selectedPrice)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid min-w-0 gap-6 lg:grid-cols-5 lg:items-start lg:gap-10">
          {/* Desktop summary panel */}
          <aside className="hidden lg:col-span-2 lg:block">
            <div className="sticky top-24 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 text-white shadow-xl">
              <div className="border-b border-zinc-800 px-6 py-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400">{PRODUCT_NAME}</p>
                <h2 className="mt-2 font-display text-xl font-semibold leading-snug">Everything you need on day one</h2>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                  Isolated tenant, role-based access, and finance modules ready after checkout.
                </p>
              </div>
              <ul className="space-y-4 px-6 py-6">
                {BENEFITS.map((b) => (
                  <li key={b.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
                      <b.icon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-zinc-100">{b.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">{b.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
              {selectedPlan && selectedPrice != null ? (
                <div className="border-t border-zinc-800 bg-zinc-900/60 px-6 py-5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Your selection</p>
                  <p className="mt-1 text-lg font-bold text-white">
                    {selectedPlan.name}{' '}
                    <span className="text-sm font-normal text-zinc-400">· {TERM_LABELS[selectedBillingTerm]}</span>
                  </p>
                  <p className="mt-1 text-2xl font-bold text-emerald-400">{formatPlanPrice(selectedPrice)}</p>
                </div>
              ) : null}
            </div>
          </aside>

          {/* Registration form */}
          <section className="min-w-0 lg:col-span-3">
            <div className="w-full min-w-0 overflow-hidden rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm sm:p-7">
              <StepProgress active={currentStep} />

              {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-800 sm:mb-6 sm:rounded-xl sm:px-4 sm:py-3 sm:text-sm" role="alert">
                  {error}
                  {pendingCheckout ? (
                    <p className="mt-2 font-medium">
                      Your payment was received. Click the button below to finish creating your workspace.
                    </p>
                  ) : null}
                </div>
              )}

              <form onSubmit={onSubmit}>
                {/* Step 1: Choose plan */}
                {currentStep === 0 && (
                  <div className="space-y-4 sm:space-y-5">
                    <div>
                      <h2 className="text-base font-semibold text-zinc-900 sm:text-lg">Choose your plan</h2>
                      <p className="mt-1 text-[13px] text-zinc-500 sm:text-sm">Select the plan that best fits your business needs.</p>
                    </div>

                    {plansLoading ? (
                      <div
                        className="grid grid-cols-1 gap-2.5 sm:gap-4"
                        aria-busy="true"
                        aria-label="Loading subscription plans"
                      >
                        {[0, 1, 2].map((i) => (
                          <div
                            key={i}
                            className="flex w-full animate-pulse items-start gap-2.5 rounded-lg border border-zinc-200 bg-white p-3.5 sm:rounded-xl sm:p-5"
                          >
                            <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-zinc-200" />
                            <div className="min-w-0 flex-1 space-y-2.5">
                              <div className="flex items-start justify-between gap-2.5">
                                <div className="min-w-0 flex-1 space-y-2">
                                  <div className="h-5 w-36 rounded bg-zinc-200 sm:w-44" />
                                  <div className="h-3 w-full rounded bg-zinc-100" />
                                </div>
                                <div className="shrink-0 space-y-1.5">
                                  <div className="h-5 w-16 rounded bg-zinc-200" />
                                  <div className="ml-auto h-2.5 w-10 rounded bg-zinc-100" />
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                <div className="h-6 w-24 rounded-full bg-zinc-100" />
                                <div className="h-6 w-28 rounded-full bg-zinc-100" />
                              </div>
                            </div>
                          </div>
                        ))}
                        <p className="flex items-center justify-center gap-2 py-1 text-[13px] font-medium text-zinc-500 sm:text-sm">
                          <Loader2 className="h-4 w-4 animate-spin text-emerald-600" aria-hidden />
                          Loading plans…
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-2.5 sm:gap-4">
                          {plans.map((p) => {
                            const on = selectedPlanId === p.planId;
                            const from = lowestOptionPrice(p);
                            const highlights = planItemLabels(p).slice(0, 3);
                            return (
                              <button
                                key={p.planId}
                                type="button"
                                onClick={() => { setSelectedPlanId(p.planId); setError(''); }}
                                className={`flex w-full items-start gap-2.5 rounded-lg border p-3.5 text-left transition sm:rounded-xl sm:p-5 ${
                                  on
                                    ? 'border-emerald-500 bg-white shadow-md ring-2 ring-emerald-500/15'
                                    : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm'
                                }`}
                              >
                                <span
                                  className={`mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full border-2 sm:h-5 sm:w-5 ${
                                    on ? 'border-emerald-600 bg-emerald-600' : 'border-zinc-300 bg-white'
                                  }`}
                                >
                                  {on ? <Check className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" strokeWidth={3} /> : null}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2.5">
                                    <div className="min-w-0">
                                      <span className="block truncate font-semibold text-zinc-900 text-base sm:text-lg">{p.name}</span>
                                      {p.description && (
                                        <p className="mt-1 text-[11px] text-zinc-500 line-clamp-2 sm:text-xs">{p.description}</p>
                                      )}
                                    </div>
                                    {from ? (
                                      <span className="shrink-0 text-right">
                                        <span className="block text-base font-bold text-emerald-700 sm:text-lg">
                                          {formatPlanPrice(from.priceInr)}
                                        </span>
                                        <span className="block text-[10px] text-zinc-500 sm:text-[11px]">/{from.label}</span>
                                      </span>
                                    ) : null}
                                  </div>
                                  {highlights.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5 sm:gap-2">
                                      {highlights.map((h, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700 sm:px-2.5 sm:py-1 sm:text-[11px]"
                                        >
                                          <CheckCircle2 className="h-3 w-3 text-emerald-600 sm:h-3.5 sm:w-3.5" />
                                          {h}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {plans.length === 0 ? (
                          <p className="mt-3 text-center text-[13px] text-amber-700 bg-amber-50 px-3 py-2.5 rounded-lg border border-amber-200 sm:mt-4 sm:text-sm sm:px-4 sm:py-3 sm:rounded-xl">
                            No plans published yet. Use a trial key or contact support.
                          </p>
                        ) : null}
                      </>
                    )}

                    <div className="flex justify-end pt-3 sm:pt-4">
                      <button
                        type="button"
                        onClick={handleNext}
                        disabled={plansLoading}
                        className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60 sm:rounded-xl sm:px-5 sm:py-3"
                      >
                        Next
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Billing term */}
                {currentStep === 1 && selectedPlan && (
                  <div className="space-y-4 sm:space-y-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-base font-semibold text-zinc-900 sm:text-lg">Select billing term</h2>
                        <p className="mt-1 text-[13px] text-zinc-500 sm:text-sm">How often you&apos;d like to be billed.</p>
                      </div>
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="flex items-center gap-1.5 text-[13px] font-medium text-zinc-500 hover:text-zinc-700 sm:text-sm"
                      >
                        <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        Back
                      </button>
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                      <PlanDurationPicker
                        plan={selectedPlan}
                        value={selectedBillingTerm}
                        onChange={setSelectedBillingTerm}
                        variant="toggle"
                      />
                      <PlanCheckoutSummary plan={selectedPlan} term={selectedBillingTerm} />
                    </div>

                    {selectedPrice != null && !payingWithTrial ? (
                      <div className="rounded-lg bg-emerald-50 px-3 py-2.5 ring-1 ring-emerald-200 sm:rounded-xl sm:px-4 sm:py-3">
                        <p className="text-[13px] font-semibold text-emerald-900 sm:text-sm">
                          Due today: <span className="text-base sm:text-lg">{formatPlanPrice(selectedPrice)}</span>
                          <span className="ml-1 font-normal text-emerald-800">({TERM_LABELS[selectedBillingTerm]})</span>
                        </p>
                      </div>
                    ) : null}

                    <label className="flex items-start gap-2.5 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 sm:rounded-xl sm:px-4 sm:py-3">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-3.5 w-3.5 rounded border-zinc-300 text-emerald-600 sm:h-4 sm:w-4"
                        checked={autoPay}
                        onChange={(e) => setAutoPay(e.target.checked)}
                      />
                      <span>
                        <span className="font-semibold text-zinc-800 text-[13px] sm:text-sm">Auto-renew</span>
                        <span className="mt-0.5 block text-[11px] text-zinc-500 sm:text-xs">
                          Charge automatically when your plan expires. Cancel anytime from Settings.
                        </span>
                      </span>
                    </label>

                    <div className="flex justify-between pt-3 sm:pt-4">
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3.5 py-2.5 text-[13px] font-semibold text-zinc-700 shadow-sm transition hover:bg-zinc-50 sm:rounded-xl sm:px-4 sm:py-3 sm:text-sm"
                      >
                        <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-500 sm:rounded-xl sm:px-5 sm:py-3"
                      >
                        Next
                        <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Account details */}
                {currentStep === 2 && (
                  <div className="space-y-4 sm:space-y-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-base font-semibold text-zinc-900 sm:text-lg">Create your account</h2>
                        <p className="mt-1 text-[13px] text-zinc-500 sm:text-sm">Organization details and login credentials.</p>
                      </div>
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="flex items-center gap-1.5 text-[13px] font-medium text-zinc-500 hover:text-zinc-700 sm:text-sm"
                      >
                        <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        Back
                      </button>
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                      <label className="block text-[13px] font-medium text-zinc-800 sm:text-sm">
                        Organization name
                        <div className={fieldWrap}>
                          <Building2 className="h-3.5 w-3.5 shrink-0 text-zinc-400 sm:h-4 sm:w-4" />
                          <input
                            required
                            className={fieldInput}
                            value={name}
                            onChange={(ev) => setName(ev.target.value)}
                            placeholder="e.g. Apex Credit Union"
                          />
                        </div>
                      </label>

                      <label className="block text-[13px] font-medium text-zinc-800 sm:text-sm">
                        Admin email
                        <div className={fieldWrap}>
                          <Mail className="h-3.5 w-3.5 shrink-0 text-zinc-400 sm:h-4 sm:w-4" />
                          <input
                            required
                            type="email"
                            className={fieldInput}
                            value={email}
                            onChange={(ev) => setEmail(ev.target.value)}
                            placeholder="you@yourfirm.com"
                          />
                        </div>
                      </label>

                      <label className="block text-[13px] font-medium text-zinc-800 sm:text-sm">
                        Mobile number
                        <span className="font-normal text-zinc-500"> (optional)</span>
                        <div className={fieldWrap}>
                          <Phone className="h-3.5 w-3.5 shrink-0 text-zinc-400 sm:h-4 sm:w-4" />
                          <input
                            type="tel"
                            inputMode="numeric"
                            className={fieldInput}
                            value={phone}
                            onChange={(ev) => setPhone(ev.target.value.replace(/[^\d+\s-]/g, ''))}
                            placeholder="10-digit mobile · e.g. 7799001866"
                            autoComplete="tel"
                          />
                        </div>
                        <p className="mt-1 text-[11px] text-zinc-500 sm:text-xs">
                          Speeds up UPI/PhonePe/GPay checkout by pre-filling your details.
                        </p>
                      </label>

                      {/* Trial key */}
                      <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50/80 sm:rounded-xl">
                        <button
                          type="button"
                          onClick={() => setShowTrialKey((v) => !v)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-3 text-left text-[13px] font-medium text-zinc-700 hover:text-zinc-900 sm:px-4 sm:py-3.5 sm:text-sm"
                        >
                          <span className="inline-flex items-center gap-2">
                            <KeyRound className="h-3.5 w-3.5 text-zinc-400 sm:h-4 sm:w-4" />
                            Have a free trial key?
                          </span>
                          <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-zinc-400 transition sm:h-4 sm:w-4 ${showTrialKey ? 'rotate-180' : ''}`} />
                        </button>
                        {showTrialKey ? (
                          <div className="border-t border-zinc-200 px-3 pb-3 pt-2.5 sm:px-4 sm:pb-4 sm:pt-3">
                            <label className="block text-[13px] font-medium text-zinc-800 sm:text-sm">
                              Trial code (6 characters)
                              <div className={fieldWrap}>
                                <KeyRound className="h-3.5 w-3.5 shrink-0 text-zinc-400 sm:h-4 sm:w-4" />
                                <input
                                  className={`${fieldInput} font-mono uppercase tracking-[0.18em]`}
                                  value={trialSecretKey}
                                  onChange={(ev) =>
                                    setTrialSecretKey(
                                      ev.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
                                    )
                                  }
                                  placeholder="ABC123"
                                  maxLength={6}
                                  autoComplete="off"
                                  spellCheck={false}
                                />
                              </div>
                            </label>
                            <p className="mt-2 text-[11px] text-zinc-500 sm:text-xs">
                              With a valid code, payment is skipped and your workspace activates on the assigned plan immediately.
                            </p>
                          </div>
                        ) : null}
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                        <label className="block text-[13px] font-medium text-zinc-800 sm:text-sm">
                          Password
                          <div className={fieldWrap}>
                            <Lock className="h-3.5 w-3.5 shrink-0 text-zinc-400 sm:h-4 sm:w-4" />
                            <input
                              required
                              type={showPassword ? 'text' : 'password'}
                              className={fieldInput}
                              value={password}
                              onChange={(ev) => setPassword(ev.target.value)}
                              autoComplete="new-password"
                              placeholder="Any password you choose"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((v) => !v)}
                              className="shrink-0 text-zinc-400 hover:text-zinc-600"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              tabIndex={-1}
                            >
                              {showPassword ? <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                            </button>
                          </div>
                        </label>

                        <label className="block text-[13px] font-medium text-zinc-800 sm:text-sm">
                          Confirm password
                          <div className={fieldWrap}>
                            <Lock className="h-3.5 w-3.5 shrink-0 text-zinc-400 sm:h-4 sm:w-4" />
                            <input
                              required
                              type={showPassword ? 'text' : 'password'}
                              className={fieldInput}
                              value={confirm}
                              onChange={(ev) => setConfirm(ev.target.value)}
                              autoComplete="new-password"
                              placeholder="Re-enter your password"
                            />
                            {confirm.length > 0 && (
                              <CheckCircle2
                                className={`h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4 ${passwordsMatch ? 'text-emerald-600' : 'text-zinc-300'}`}
                              />
                            )}
                          </div>
                        </label>
                      </div>

                      {googleClientId ? (
                        <div className="mt-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3.5 sm:rounded-xl sm:p-4">
                          <p className="text-[13px] font-medium text-zinc-700 sm:text-sm">
                            Or continue with Google
                          </p>
                          <div className="mt-3">
                            <GoogleAuthButton
                              text="signup_with"
                              onCredential={onGoogle}
                              onError={(err) => setError(err?.message || 'Google sign-up failed.')}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-col gap-3 pt-5 sm:pt-6">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-emerald-600 to-emerald-800 py-3 text-sm font-semibold text-white shadow-md transition hover:from-emerald-500 hover:to-emerald-700 disabled:opacity-60 sm:rounded-xl sm:py-3.5"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin sm:h-4 sm:w-4" />
                            Processing…
                          </>
                        ) : pendingCheckout ? (
                          <>
                            Finish workspace setup
                            <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          </>
                        ) : (
                          <>
                            {trialSecretKey.trim()
                              ? 'Activate trial workspace'
                              : selectedPrice
                              ? `Pay ${formatPlanPrice(selectedPrice)} & create workspace`
                              : 'Create workspace'}
                            <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          </>
                        )}
                      </button>

                      <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-zinc-500 sm:text-xs">
                        <ShieldCheck className="h-3 w-3 text-emerald-600 sm:h-3.5 sm:w-3.5" />
                        Secure checkout via Razorpay · UPI, PhonePe, GPay &amp; cards
                      </p>

                      <p className="mt-1 text-center text-[13px] text-zinc-600 sm:text-sm">
                        Already subscribed?{' '}
                        <button
                          type="button"
                          className="font-semibold text-emerald-700 hover:underline"
                          onClick={() => navigate('/login')}
                        >
                          Sign in
                        </button>
                      </p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
